import { Component, Input, OnChanges, OnInit, SimpleChanges } from "@angular/core";
import { FormBuilder, FormControl, FormGroup } from "@angular/forms";
import { Observable, of } from "rxjs";
import { tap } from "rxjs/operators";
import { VngToggleComponent } from "@vengreso/angular-components";
import { DialogService } from "src/app/core/core-services/services/dialog.service";
import { WarningDialogComponent } from "src/app/core/core-services/warning-dialog/warning-dialog.component";
import { BaseSettingsSectionComponent } from "../base-settings-section.component";
import { ExtensionSettingsService } from "../extension-settings.service";
import {
  ALL_DISABLED_SITE_LIST_KEYS,
  DisabledSiteFeature,
  DisabledSiteListKey,
  DisabledSiteLists,
  FEATURE_LISTS,
} from "../extension-settings.models";
import { domainValidator, normalizeDomain } from "../domain.utils";
import {
  addDomainToList,
  isDomainInList,
  platformPluginLabel,
  removeDomainFromList,
} from "./site-lists.utils";

/** One site row in the manager: the union row across the five lists. */
export interface DisabledSiteRow {
  domain: string;
  shortcutsDisabled: boolean;
  grammarDisabled: boolean;
  rewriteDisabled: boolean;
  pluginDisabled: boolean;
  /** Automatic Sentence Rewriter (issue#550) — off by default globally, so its
   * column locks the same way the Paragraph Rewriter's does when the master
   * card switch is off. */
  sentenceRewriteDisabled: boolean;
  /** "Gmail" | "LinkedIn" | "Outlook" when the plugin applies here, else null. */
  pluginLabel: string | null;
  /** Every feature the user controls on this site is off. */
  fullyDisabled: boolean;
  /** Some but not all controllable features are off. */
  partiallyDisabled: boolean;
}

/**
 * Unified "Disabled sites" manager: rows are the union of domains across the
 * five per-site blacklists, columns are per-feature toggles (checked = the
 * feature is ON for that site, i.e. the domain is absent from that list), plus
 * a per-row master switch mirroring the extension popup's
 * enableSite/disablePermanent (SiteToggle.tsx). Edits are local until Save,
 * which batches every changed list into a single PUT v1/user/setting.
 */
@Component({
  selector: "app-disabled-sites-settings",
  templateUrl: "./disabled-sites-settings.component.html",
  styleUrls: ["./disabled-sites-settings.component.scss"],
  standalone: false,
})
export class DisabledSitesSettingsComponent
  extends BaseSettingsSectionComponent
  implements OnInit, OnChanges
{
  @Input() shortcutsBlockedDomains: string[] = [];
  @Input() grammarBlockedDomains: string[] = [];
  @Input() rewriteBlockedDomains: string[] = [];
  @Input() pluginBlockedDomains: string[] = [];
  @Input() sentenceRewriteBlockedDomains: string[] = [];

  /** Company admin forces the FlyGrammar state (worker ignores user writes). */
  @Input() grammarAdminOverride = false;
  /** Company admin forces the Paragraph Rewriter state. */
  @Input() rewriteAdminOverride = false;
  /** Company admin forces the Sentence Rewriter state (override_user_fly_sentence_rewrite). */
  @Input() sentenceRewriteAdminOverride = false;
  /** The FlyGrammar card's master switch — per-site toggles are moot when off. */
  @Input() grammarGloballyEnabled = true;
  /** The Paragraph Rewriter card's master switch. */
  @Input() rewriteGloballyEnabled = true;
  /** The Sentence Rewriter card's master switch — defaults false (opt-in feature). */
  @Input() sentenceRewriteGloballyEnabled = false;
  /** Plan gate: disabling FlyGrammar (globally or per-site) needs this. */
  @Input() canDisableFlyGrammar = true;
  /**
   * Issue #561's product gate: whether the signed-in user is entitled to
   * FlyMSG Sentence Rewriter (`userHasSentenceRewriterAccess`). Controls only
   * whether the Sentence Rewriter column renders — the other four feature
   * columns and "All features" are unaffected either way.
   */
  @Input() sentenceRewriterAccessGranted = false;

  addSiteControl = new FormControl<string>("", domainValidator);

  private working: DisabledSiteLists;
  private lastSaved: DisabledSiteLists;

  constructor(
    private readonly fb: FormBuilder,
    private readonly settingsService: ExtensionSettingsService,
    private readonly dialog: DialogService
  ) {
    super();
  }

  ngOnInit(): void {
    this.lastSaved = this.listsFromInputs();
    this.working = cloneLists(this.lastSaved);
    super.ngOnInit();
  }

  ngOnChanges(changes: SimpleChanges): void {
    const listInputs = [
      "shortcutsBlockedDomains",
      "grammarBlockedDomains",
      "rewriteBlockedDomains",
      "pluginBlockedDomains",
      "sentenceRewriteBlockedDomains",
    ];
    const changed = listInputs.some((key) => changes[key] && !changes[key].firstChange);
    if (changed && this.form) {
      // Server-side truth moved (extension pushed fmsUpdateSettingsFE or a
      // reload re-hydrated) — same overwrite-and-reset the sibling cards do.
      this.lastSaved = this.listsFromInputs();
      this.working = cloneLists(this.lastSaved);
      this.markPristine();
    }
  }

  buildForm(): FormGroup {
    // The base class derives dirtiness from this form; list edits are
    // structural (not control values), so the group stays empty and dirtiness
    // is flagged manually — the same pattern domain-blacklist-settings used.
    return this.fb.group({});
  }

  // ── Row model ──────────────────────────────────────────────────────────────

  get grammarControllable(): boolean {
    return !this.grammarAdminOverride && this.grammarGloballyEnabled;
  }

  get rewriteControllable(): boolean {
    return !this.rewriteAdminOverride && this.rewriteGloballyEnabled;
  }

  get sentenceRewriteControllable(): boolean {
    return !this.sentenceRewriteAdminOverride && this.sentenceRewriteGloballyEnabled;
  }

  /**
   * Issue #562/QA F1: the Sentence Rewriter column is hidden from ungranted
   * users (`sentenceRewriterAccessGranted` false), but `sentenceRewriteControllable`
   * alone does not consult that gate — it only reflects the master-switch/admin
   * state. Every place that WRITES `fly_sentence_rewrite_blocked_domains`, or
   * folds the sentence column into the "All features" aggregate, must check
   * this getter instead so a stored-but-hidden `fly_sentence_rewrite: "enabled"`
   * state can never surface an unreachable "All features" state or a silent
   * write: `buildRow`'s `applicable` list, `addSite`, and both `onSiteToggle`
   * branches.
   *
   * QA round 2 (H-01/H-02): NOT used by `hasVisibleMembership` — that method
   * answers "does this row render a control for this list", which tracks the
   * column's own render gate (`sentenceRewriterAccessGranted` alone, same as
   * the *ngIf), not whether writes are currently enabled. Using the write
   * gate there made a GRANTED user's SR-only blocked domain vanish from the
   * table whenever the SR master switch was off, and let re-enabling
   * shortcuts on a shared domain silently wipe their SR block.
   */
  get sentenceRewriteWritable(): boolean {
    return this.sentenceRewriteControllable && this.sentenceRewriterAccessGranted;
  }

  get rows(): DisabledSiteRow[] {
    const domains = new Set<string>();
    for (const key of ALL_DISABLED_SITE_LIST_KEYS) {
      for (const entry of this.working?.[key] ?? []) {
        const host = normalizeDomain(entry);
        if (host !== null) domains.add(host);
      }
    }
    return Array.from(domains)
      .filter((domain) => this.hasVisibleMembership(domain))
      .sort()
      .map((domain) => this.buildRow(domain));
  }

  trackByDomain(_index: number, row: DisabledSiteRow): string {
    return row.domain;
  }

  private buildRow(domain: string): DisabledSiteRow {
    const shortcutsDisabled = isDomainInList(domain, this.working[FEATURE_LISTS.shortcuts]);
    const grammarDisabled = isDomainInList(domain, this.working[FEATURE_LISTS.grammar]);
    const rewriteDisabled = isDomainInList(domain, this.working[FEATURE_LISTS.rewrite]);
    const pluginDisabled = isDomainInList(domain, this.working[FEATURE_LISTS.plugin]);
    const sentenceRewriteDisabled = isDomainInList(domain, this.working[FEATURE_LISTS.sentence]);
    const pluginLabel = platformPluginLabel(domain);

    // Only count what the user actually controls on this site — the popup's
    // rule (SiteToggle.tsx): shortcuts always; grammar/rewrite/sentence when
    // not globally off or admin-forced; plugin only where it exists. The plan
    // gate is one-directional: it blocks disabling grammar but never
    // re-enabling, so a gated-but-not-disabled grammar must not count toward
    // "fully off" (no write path can get it there), while an already-disabled
    // one still does.
    const applicable: boolean[] = [shortcutsDisabled];
    if (this.grammarControllable && (this.canDisableFlyGrammar || grammarDisabled)) {
      applicable.push(grammarDisabled);
    }
    if (this.rewriteControllable) applicable.push(rewriteDisabled);
    if (this.sentenceRewriteWritable) applicable.push(sentenceRewriteDisabled);
    if (pluginLabel !== null) applicable.push(pluginDisabled);

    const disabledCount = applicable.filter(Boolean).length;
    return {
      domain,
      shortcutsDisabled,
      grammarDisabled,
      rewriteDisabled,
      pluginDisabled,
      sentenceRewriteDisabled,
      pluginLabel,
      fullyDisabled: disabledCount === applicable.length,
      partiallyDisabled: disabledCount > 0 && disabledCount < applicable.length,
    };
  }

  // ── Edits (local until Save) ───────────────────────────────────────────────

  addSite(): void {
    const host = normalizeDomain(this.addSiteControl.value);
    if (this.addSiteControl.invalid || host === null) {
      // Blank input passes the validator (the button stays disabled for it),
      // but an Enter-key submit must not be a silent no-op — surface the same
      // inline message the invalid path shows. Typing re-runs the validator
      // and clears it.
      this.addSiteControl.setErrors({ domain: true });
      this.addSiteControl.markAsTouched();
      return;
    }
    // "Disable on this site" semantics from the popup's disablePermanent:
    // shortcuts always; plugin only where it exists (a non-platform entry
    // would be invisible residue the row model ignores); grammar/rewrite only
    // when the user controls them (and, for grammar, when the plan allows
    // disabling it).
    this.setListMembership(FEATURE_LISTS.shortcuts, host, true);
    if (platformPluginLabel(host) !== null) {
      this.setListMembership(FEATURE_LISTS.plugin, host, true);
    }
    if (this.grammarControllable && this.canDisableFlyGrammar) {
      this.setListMembership(FEATURE_LISTS.grammar, host, true);
    }
    if (this.rewriteControllable) {
      this.setListMembership(FEATURE_LISTS.rewrite, host, true);
    }
    if (this.sentenceRewriteWritable) {
      this.setListMembership(FEATURE_LISTS.sentence, host, true);
    }
    this.addSiteControl.reset("");
    this.flagDirty();
  }

  /**
   * One feature toggle changed. `enabled` is the toggle's new checked value
   * (true = feature ON for this site = remove from that list).
   */
  onFeatureToggle(
    row: DisabledSiteRow,
    feature: DisabledSiteFeature,
    enabled: boolean,
    toggle?: VngToggleComponent
  ): void {
    if (feature === "grammar" && !enabled && !this.canDisableFlyGrammar) {
      // Same plan-gate the FlyGrammar card enforces on its master switch.
      toggle?.writeValue(true);
      this.openGrammarPlanWarning();
      return;
    }
    this.setListMembership(FEATURE_LISTS[feature], row.domain, !enabled);
    if (enabled && !this.hasVisibleMembership(row.domain)) {
      // Re-enabling the last visible feature must make the row disappear —
      // clear invisible residue too (a plugin entry on a non-platform host,
      // e.g. written by the popup's disablePermanent), mirroring the popup's
      // enableSite which removes the domain from all four lists.
      this.clearAllLists(row.domain);
    }
    this.flagDirty();
  }

  /**
   * Master per-row switch — the popup's enableSite/disablePermanent pair.
   * ON (true) re-enables everything: the domain leaves all four lists (the row
   * disappears). OFF disables every feature the user controls here.
   */
  onSiteToggle(row: DisabledSiteRow, enabled: boolean): void {
    if (enabled) {
      // enableSite: shortcuts + plugin always; grammar/rewrite when controllable.
      this.setListMembership(FEATURE_LISTS.shortcuts, row.domain, false);
      this.setListMembership(FEATURE_LISTS.plugin, row.domain, false);
      if (this.grammarControllable) {
        this.setListMembership(FEATURE_LISTS.grammar, row.domain, false);
      }
      if (this.rewriteControllable) {
        this.setListMembership(FEATURE_LISTS.rewrite, row.domain, false);
      }
      if (this.sentenceRewriteWritable) {
        this.setListMembership(FEATURE_LISTS.sentence, row.domain, false);
      }
    } else {
      // disablePermanent: same writable set, opposite direction; plugin only
      // where it exists, and the grammar list additionally needs the plan to
      // allow disabling.
      this.setListMembership(FEATURE_LISTS.shortcuts, row.domain, true);
      if (row.pluginLabel !== null) {
        this.setListMembership(FEATURE_LISTS.plugin, row.domain, true);
      }
      if (this.grammarControllable && this.canDisableFlyGrammar) {
        this.setListMembership(FEATURE_LISTS.grammar, row.domain, true);
      }
      if (this.rewriteControllable) {
        this.setListMembership(FEATURE_LISTS.rewrite, row.domain, true);
      }
      if (this.sentenceRewriteWritable) {
        this.setListMembership(FEATURE_LISTS.sentence, row.domain, true);
      }
    }
    this.flagDirty();
  }

  private setListMembership(key: DisabledSiteListKey, domain: string, disabled: boolean): void {
    this.working[key] = disabled
      ? addDomainToList(this.working[key], domain)
      : removeDomainFromList(this.working[key], domain);
  }

  /**
   * Whether the domain is disabled in any list the row actually renders a
   * control (or a locked control) for. A plugin entry on a non-platform host
   * renders as a dash, has no functional effect anywhere, and must not keep a
   * row alive on its own — that is the state QA S-09/G-08 flagged.
   *
   * The sentence-rewrite arm checks `sentenceRewriterAccessGranted` (the
   * column's own *ngIf gate), NOT `sentenceRewriteWritable` — a GRANTED
   * user's SR-only blocked domain must keep its row even while the SR master
   * switch is off (locked-but-visible, same as the sibling grammar/rewrite
   * columns), matching every other arm here. QA round 2 (H-01/H-02) caught
   * the write-gate version silently dropping rows and, via
   * `hasVisibleMembership` gating `clearAllLists` in `onFeatureToggle`,
   * silently wiping the SR list too.
   */
  private hasVisibleMembership(domain: string): boolean {
    return (
      isDomainInList(domain, this.working[FEATURE_LISTS.shortcuts]) ||
      isDomainInList(domain, this.working[FEATURE_LISTS.grammar]) ||
      isDomainInList(domain, this.working[FEATURE_LISTS.rewrite]) ||
      (this.sentenceRewriterAccessGranted &&
        isDomainInList(domain, this.working[FEATURE_LISTS.sentence])) ||
      (platformPluginLabel(domain) !== null &&
        isDomainInList(domain, this.working[FEATURE_LISTS.plugin]))
    );
  }

  private clearAllLists(domain: string): void {
    for (const key of ALL_DISABLED_SITE_LIST_KEYS) {
      this.working[key] = removeDomainFromList(this.working[key], domain);
    }
  }

  private flagDirty(): void {
    this.form.markAsDirty();
    this.isDirty = true;
    this.dirtyChange.emit(true);
  }

  private openGrammarPlanWarning(): void {
    this.dialog.openDialogComponent(
      WarningDialogComponent,
      { type: "fly_grammar", showTheStarter: false, error: "disableFlyGrammarPerSite" },
      "800px"
    );
  }

  // ── Persistence ────────────────────────────────────────────────────────────

  getSaveObservable(): Observable<any> {
    const payload: Partial<DisabledSiteLists> = {};
    for (const key of ALL_DISABLED_SITE_LIST_KEYS) {
      if (!sameList(this.working[key], this.lastSaved[key])) {
        payload[key] = [...this.working[key]];
      }
    }
    if (Object.keys(payload).length === 0) {
      return of(null);
    }
    // One PUT for however many lists changed — a master-switch flip is a single
    // round-trip and a single extension update ping, never four.
    return this.settingsService.saveDisabledSites(payload).pipe(
      tap(() => {
        this.lastSaved = cloneLists(this.working);
      })
    );
  }

  resetToLastSaved(): void {
    this.working = cloneLists(this.lastSaved);
    this.addSiteControl.reset("");
  }

  private listsFromInputs(): DisabledSiteLists {
    return {
      blocked_domains: [...(this.shortcutsBlockedDomains ?? [])],
      fly_grammar_blocked_domains: [...(this.grammarBlockedDomains ?? [])],
      fly_rewrite_blocked_domains: [...(this.rewriteBlockedDomains ?? [])],
      plugin_blocked_domains: [...(this.pluginBlockedDomains ?? [])],
      fly_sentence_rewrite_blocked_domains: [...(this.sentenceRewriteBlockedDomains ?? [])],
    };
  }
}

function cloneLists(lists: DisabledSiteLists): DisabledSiteLists {
  return {
    blocked_domains: [...lists.blocked_domains],
    fly_grammar_blocked_domains: [...lists.fly_grammar_blocked_domains],
    fly_rewrite_blocked_domains: [...lists.fly_rewrite_blocked_domains],
    plugin_blocked_domains: [...lists.plugin_blocked_domains],
    fly_sentence_rewrite_blocked_domains: [...lists.fly_sentence_rewrite_blocked_domains],
  };
}

function sameList(a: string[], b: string[]): boolean {
  return a.length === b.length && a.every((value, index) => value === b[index]);
}
